import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';

import authSlice from '@/features/admin/authSlice';
import { baseApi } from '@/services/api/baseApi';

// Declared separately so the state shape can be named before a store exists,
// which is what lets `preloadedState` be typed rather than cast.
const rootReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  auth: authSlice.reducer,
});

export type RootState = ReturnType<typeof rootReducer>;

/**
 * A fresh store per call: the prerenderer builds one per route so no state leaks
 * between pages, while the browser creates exactly one.
 *
 * @param preloadedState state captured during prerendering, replayed on hydration
 *                       so the first paint after hydration matches the static
 *                       HTML instead of flashing a skeleton
 */
export function createStore(preloadedState?: Partial<RootState>) {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(baseApi.middleware),
    preloadedState,
  });

  setupListeners(store.dispatch);
  return store;
}

export type AppStore = ReturnType<typeof createStore>;
export type AppDispatch = AppStore['dispatch'];

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
